import { ArrowRight, GitBranch, Lock, Plus, Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { NodeIcon } from "@/components/NodeIcon";
import { NewNodeDialog } from "@/components/nodes/NewNodeDialog";
import type { RunResult } from "@/api/adapt";
import type { HomeDto, NodeCardDto } from "@/api/types";
import { chanceLabel, changeText, headlineValueText } from "@/components/shell/scenarioSummary";
import { ago, useViewResult } from "@/components/shell/view";
import { useHome, useTemplates } from "@/hooks/world";
import { Button } from "@/components/ui/button";
import { useCanEdit } from "@/model/access";
import { clientPrefix } from "@/lib/format";
import { fmtByDate, idx } from "@/lib/months";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";
import type { ModelNode } from "@/model/types";

const SPARK_END = idx("2030-12");

/** Chance-by-date curve for events; likely line with its Low–High band for metrics. Scenario runs draw in amber. */
function Spark({ node, r, scenario, w = 120, h = 32 }: { node: ModelNode; r: RunResult | null; scenario?: boolean; w?: number; h?: number }) {
	if (!r) return <svg width={w} height={h} aria-hidden />;
	const stroke = scenario ? "var(--scenario)" : "var(--model)";
	const x = (t: number) => (t / SPARK_END) * w;
	const ts: number[] = [];
	for (let t = 0; t <= SPARK_END; t += 2) ts.push(t);
	if (node.kind === "event") {
		const F = r.events[node.id]?.F;
		if (!F) return <svg width={w} height={h} aria-hidden />;
		return (
			<svg width={w} height={h} className="overflow-visible" aria-hidden>
				<path d={`M0 ${h} H${w}`} stroke="var(--grid)" />
				<polyline points={ts.map((t) => `${x(t)},${h - F[t] * h}`).join(" ")} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" />
			</svg>
		);
	}
	const q = r.metrics[node.id]?.q;
	if (!q) return <svg width={w} height={h} aria-hidden />;
	let lo = Infinity;
	let hi = -Infinity;
	for (const t of ts) {
		lo = Math.min(lo, q[1][t]);
		hi = Math.max(hi, q[3][t]);
	}
	const span = hi - lo || 1;
	const y = (v: number) => h - ((v - lo) / span) * h;
	const band = [...ts.map((t) => `${x(t)},${y(q[3][t])}`), ...[...ts].reverse().map((t) => `${x(t)},${y(q[1][t])}`)].join(" ");
	return (
		<svg width={w} height={h} className="overflow-visible" aria-hidden>
			<polygon points={band} fill={stroke} fillOpacity="0.15" />
			<polyline points={ts.map((t) => `${x(t)},${y(q[2][t])}`).join(" ")} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" />
		</svg>
	);
}

/** The latest activity per node, newest first (from `/home`). */
function whatMoved(home: HomeDto | null, nodes: Record<string, ModelNode>, max = 4) {
	const seen = new Set<string>();
	const out: { node: ModelNode; card: NodeCardDto | undefined; text: string; at: string }[] = [];
	for (const a of home?.activity ?? []) {
		const n = a.node ? nodes[a.node] : undefined;
		if (!n || seen.has(n.id) || a.undone) continue;
		seen.add(n.id);
		out.push({
			node: n,
			card: home?.nodes.find((c) => c.handle === n.id),
			text: `${clientPrefix(a.origin, a.actor.client_name)}${a.summary}`,
			at: a.created_at,
		});
		if (out.length === max) break;
	}
	return out;
}

const SOURCE: Record<string, string> = { manual: "Own forecast", market: "Market", crowd: "Crowd", feed: "Data feed", formula: "Calculated" };
function sourceLabel(n: ModelNode) {
	if ((n.kind === "event" && n.definition) || (n.kind === "metric" && n.formula)) return "Calculated";
	if (n.kind === "event" && n.market) return "Market";
	return SOURCE[n.source] ?? n.source;
}

function Section({ title, note, children }: { title: string; note?: React.ReactNode; children: React.ReactNode }) {
	return (
		<section className="space-y-3">
			<div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
				<h2 className="text-lg font-semibold">{title}</h2>
				{note}
			</div>
			{children}
		</section>
	);
}

export function HomePage() {
	const world = useStore((s) => s.world);
	const view = useViewResult();
	const home = useHome(view.scenario?.id);
	const scen = !!view.scenario;
	const nodes = Object.values(world.nodes);
	const moved = whatMoved(home.data, world.nodes);
	// Followed nodes, or the most connected ones when nothing is followed (the API chooses).
	const heads = (home.data?.headlines.cards ?? [])
		.map((c) => ({ card: c, node: world.nodes[c.handle] }))
		.filter((x): x is { card: NodeCardDto; node: ModelNode } => !!x.node);
	const headIds = new Set(heads.map((h) => h.node.id));
	const rest = (home.data?.nodes ?? []).filter((c) => !headIds.has(c.handle) && world.nodes[c.handle]);
	const firstRun = !!home.data && moved.length === 0;
	const startWith = nodes.find((n) => n.kind === "event" && n.visibility === "public" && n.market) ?? nodes.find((n) => n.visibility === "public");

	return (
		<div className="space-y-10">
			<header>
				<h1 className="text-3xl font-semibold tracking-tight">Worldview</h1>
				{firstRun && startWith && (
					<p className="mt-2 max-w-[62ch] font-serif text-lg text-foreground/80">
						Every forecast here is a curve you can drag, and links carry each change downstream.{" "}
						<Link to={`/node/${startWith.id}`} className="font-sans text-base font-medium whitespace-nowrap text-foreground underline underline-offset-4">
							Start with a public event <ArrowRight className="inline size-4" />
						</Link>
					</p>
				)}
			</header>
			{nodes.length === 0 && <FirstRun />}

			{moved.length > 0 && (
				<Section title="What moved">
					{/* A swipeable row on phones, a grid from `sm`. */}
					<div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 xl:grid-cols-4">
						{moved.map(({ node: n, card, text, at }) => (
							<Link
								key={n.id}
								to={`/node/${n.id}`}
								className="flex w-[78%] shrink-0 snap-start flex-col rounded-xl border bg-card p-4 transition-colors hover:border-ink/40 sm:w-auto"
							>
								<div className="flex items-center gap-2 text-sm font-medium">
									<NodeIcon node={n} className="text-muted-foreground" />
									<span className="truncate">{n.title}</span>
								</div>
								<div className="mt-2 flex items-baseline gap-1.5">
									<span className={cn("text-2xl font-semibold tabular-nums", scen && "text-scenario")}>{headlineValueText(card?.headline, card?.unit)}</span>
									{card?.headline && <span className="text-xs text-muted-foreground">by {fmtByDate(card.headline.date)}</span>}
								</div>
								<p className="mt-1 line-clamp-2 text-sm text-foreground/80">{text}</p>
								<p className="mt-auto pt-2 text-xs text-muted-foreground">{ago(at)}</p>
							</Link>
						))}
					</div>
				</Section>
			)}

			{heads.length > 0 && (
				<Section
					title={home.data?.headlines.source === "followed" ? "Following" : "Headline events"}
					note={
						scen && (
							<span className="text-sm text-muted-foreground">
								Numbers in <span className="font-medium text-foreground">{view.scenario!.name}</span>
							</span>
						)
					}
				>
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{heads.map(({ node: n, card }) => (
							<Link key={n.id} to={`/node/${n.id}`} className="flex flex-col rounded-xl border bg-card p-4 transition-colors hover:border-ink/40">
								<div className="flex items-center gap-2 text-sm font-medium">
									<NodeIcon node={n} className="text-muted-foreground" />
									<span className="truncate">{n.title}</span>
									{n.visibility === "private" && <Lock className="size-3 shrink-0 text-muted-foreground" aria-label="Private" />}
								</div>
								<div className="mt-3 flex items-end justify-between gap-3">
									<div>
										<div
											className={cn(
												"text-4xl leading-none font-semibold tracking-tight tabular-nums",
												scen ? "text-scenario" : "text-model",
												home.pending && "opacity-60",
											)}
										>
											{headlineValueText(card.headline, card.unit)}
										</div>
										<div className="mt-1.5 text-sm text-muted-foreground">
											{n.kind === "metric" ? "likely, " : ""}by {card.headline ? fmtByDate(card.headline.date) : "…"}
										</div>
									</div>
									<Spark node={n} r={view.data} scenario={scen} />
								</div>
							</Link>
						))}
					</div>
				</Section>
			)}

			{/* Scenarios change events and metrics, so they mean nothing until there is at least one. */}
			{nodes.length > 0 && <ScenarioCards home={home.data} />}

			{rest.length > 0 && <EverythingElse cards={rest} scen={scen} />}
		</div>
	);
}

/** What each kind of node is, in the words a first-time person needs to choose between them. */
const KINDS = [
	{
		kind: "event",
		title: "An event",
		what: "Something that happens, or does not.",
		how: "You say the chance it happens by each date.",
		eg: "“We lose our biggest client”",
	},
	{
		kind: "metric",
		title: "A metric",
		what: "A number that changes over time.",
		how: "You say its Low, Likely and High value for each month.",
		eg: "“Monthly revenue”",
	},
] as const;

/** An empty workspace: create a first event or metric, start from a template, or ask Claude. Scenarios come later. */
function FirstRun() {
	const canEdit = useCanEdit();
	const templates = useTemplates(canEdit);
	const install = useStore((s) => s.installTemplate);
	const [busy, setBusy] = useState<string | null>(null);
	const [creating, setCreating] = useState<"event" | "metric" | null>(null);
	if (!canEdit)
		return (
			<p className="max-w-[62ch] font-serif text-lg text-foreground/80">Nothing here yet. The people who can edit this workspace haven’t added anything.</p>
		);
	const list = templates.data?.templates ?? [];
	return (
		<div className="space-y-10">
			<section className="space-y-4">
				<p className="max-w-[62ch] font-serif text-lg text-foreground/80">Nothing here yet. What do you want to forecast first?</p>
				<div className="grid gap-3 sm:grid-cols-2">
					{KINDS.map((k) => (
						<button
							key={k.kind}
							type="button"
							onClick={() => setCreating(k.kind)}
							className="group flex flex-col gap-2 rounded-xl border bg-card p-5 text-left transition-colors hover:border-ink/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
						>
							<span className="flex items-center gap-2.5 text-lg font-semibold">
								<NodeIcon node={{ kind: k.kind }} className="text-muted-foreground" />
								{k.title}
							</span>
							<span className="text-foreground/85">{k.what}</span>
							<span className="text-sm text-muted-foreground">
								{k.how} For example, {k.eg}.
							</span>
							<span className="mt-auto flex items-center gap-1 pt-2 text-sm font-medium">
								<Plus className="size-4" aria-hidden /> New {k.kind}{" "}
								<ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
							</span>
						</button>
					))}
				</div>
				<p className="text-sm text-muted-foreground">You can also ask Claude to build a model for you.</p>
				<NewNodeDialog open={!!creating} onOpenChange={(v) => !v && setCreating(null)} kind={creating ?? "event"} />
			</section>
			{list.length > 0 && (
				<Section title="Or start from a template">
					<div className="grid gap-3 sm:grid-cols-2">
						{list.map((t) => (
							<div key={t.name} className="flex flex-col gap-3 rounded-xl border bg-card p-4">
								<div>
									<h3 className="font-semibold">{t.title}</h3>
									<p className="mt-1 text-sm text-muted-foreground">{t.description}</p>
								</div>
								<Button
									className="mt-auto self-start"
									variant="outline"
									disabled={busy !== null}
									onClick={async () => {
										setBusy(t.name);
										await install(t.name);
										setBusy(null);
									}}
								>
									{busy === t.name ? "Setting it up…" : "Start from this"}
								</Button>
							</div>
						))}
					</div>
				</Section>
			)}
		</div>
	);
}

function ScenarioCards({ home }: { home: HomeDto | null }) {
	const world = useStore((s) => s.world);
	const active = useStore((s) => s.activeScenario);
	return (
		<Section
			title="Scenarios"
			note={
				<Link to="/graph" className="text-sm text-muted-foreground hover:text-foreground">
					See everything on the graph
				</Link>
			}
		>
			{world.scenarios.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No scenarios yet. A scenario sets its own values for any events and metrics; everything downstream follows.
				</p>
			) : (
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
					{world.scenarios.map((sc) => {
						const card = home?.scenarios.find((c) => c.id === sc.id);
						const chance = chanceLabel(sc, card);
						const change = card?.biggest_change;
						return (
							<Link
								key={sc.id}
								to={`/scenario/${sc.id}`}
								className={cn("flex flex-col rounded-xl border bg-card p-4 transition-colors hover:border-ink/40", active === sc.id && "border-scenario")}
							>
								<div className="flex items-start justify-between gap-2">
									<div className="flex min-w-0 items-center gap-2 font-semibold">
										<GitBranch className="size-4 shrink-0 text-scenario" aria-hidden />
										<span className="truncate">{sc.name}</span>
									</div>
									<span className="shrink-0 rounded-full bg-scenario/12 px-2 py-0.5 text-xs font-medium whitespace-nowrap">{chance ?? "…"}</span>
								</div>
								{sc.description && <p className="mt-1 font-serif text-foreground/75">{sc.description}</p>}
								<div className="mt-auto pt-3 text-sm">
									{!sc.levers.length ? (
										<span className="text-muted-foreground">Nothing set yet</span>
									) : !card ? (
										<span className="text-muted-foreground">Working it out…</span>
									) : change ? (
										<span>
											<span className="text-muted-foreground">Biggest change: </span>
											<span className="font-medium">{change.title}</span> <span className="tabular-nums">{changeText(change)}</span>
										</span>
									) : (
										<span className="text-muted-foreground">
											No headline moves; sets {card.lever_count} {card.lever_count === 1 ? "node" : "nodes"}
										</span>
									)}
								</div>
							</Link>
						);
					})}
				</div>
			)}
		</Section>
	);
}

function EverythingElse({ cards, scen }: { cards: NodeCardDto[]; scen: boolean }) {
	const nodes = useStore((s) => s.world.nodes);
	const [q, setQ] = useState("");
	const [kind, setKind] = useState<"all" | "event" | "metric">("all");
	const query = q.trim().toLowerCase();
	const shown = cards
		.map((c) => ({ c, n: nodes[c.handle] }))
		.filter(({ n }) => kind === "all" || n.kind === kind)
		.filter(({ n }) => !query || `${n.title} ${n.question} ${n.tags.join(" ")}`.toLowerCase().includes(query))
		.sort((a, b) => (a.n.kind === b.n.kind ? a.n.title.localeCompare(b.n.title) : a.n.kind === "event" ? -1 : 1));

	return (
		<Section title="Everything else">
			<div className="flex flex-wrap items-center gap-2">
				<label className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border bg-card px-3 sm:max-w-xs">
					<Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
					<input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder="Filter"
						aria-label="Filter events and metrics"
						className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
					/>
				</label>
				<ToggleGroup type="single" value={kind} onValueChange={(v) => v && setKind(v as typeof kind)} variant="outline" size="sm" aria-label="Kind">
					<ToggleGroupItem value="all">All</ToggleGroupItem>
					<ToggleGroupItem value="event">Events</ToggleGroupItem>
					<ToggleGroupItem value="metric">Metrics</ToggleGroupItem>
				</ToggleGroup>
			</div>
			<div className="overflow-hidden rounded-xl border bg-card">
				<table className="w-full text-sm">
					<thead className="border-b text-left text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-2 font-medium">Name</th>
							<th className="px-4 py-2 text-right font-medium">{scen ? "In scenario" : "Headline"}</th>
							<th className="hidden px-4 py-2 font-medium sm:table-cell">Source</th>
						</tr>
					</thead>
					<tbody className="divide-y">
						{shown.map(({ c, n }) => (
							<tr key={n.id} className="relative hover:bg-muted/40">
								<td className="w-full max-w-0 px-4 py-2.5">
									<Link to={`/node/${n.id}`} className="flex items-center gap-2.5 after:absolute after:inset-0">
										<NodeIcon node={n} className="text-muted-foreground" />
										<span className="truncate font-medium">{n.title}</span>
										{n.visibility === "private" && <Lock className="size-3 shrink-0 text-muted-foreground" aria-label="Private" />}
									</Link>
								</td>
								<td className="px-4 py-2.5 text-right whitespace-nowrap">
									<span className="font-semibold tabular-nums">{headlineValueText(c.headline, c.unit)}</span>
									{c.headline && <span className="ml-1.5 text-xs text-muted-foreground">by {fmtByDate(c.headline.date)}</span>}
								</td>
								<td className="hidden px-4 py-2.5 whitespace-nowrap text-muted-foreground sm:table-cell">{sourceLabel(n)}</td>
							</tr>
						))}
						{shown.length === 0 && (
							<tr>
								<td colSpan={3} className="px-4 py-3 text-muted-foreground">
									Nothing matches.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</Section>
	);
}
